/**
 * Pre-Flight Check Page - Intelligence Section
 * Audit readiness assessment with compliance scoring and gap identification
 */
import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  FileText,
  Users,
  Database,
  Target,
  ArrowRight,
  Loader2,
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { apiFetch } from '../../services/api_service';


const SCORE_COLORS = {
  excellent: { color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
  good: { color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30' },
  warning: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
  critical: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' },
};

function getScoreLevel(score) {
  if (score >= 90) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'warning';
  return 'critical';
}

export default function PreFlightCheckPage() {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const fetchAssessment = async () => {
    try {
      const data = await apiFetch(`/v1/ml/audit-readiness`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (data && !data._error) {
        setAssessment(data);
      } else {
        setAssessment(getDefaultAssessment());
      }
    } catch {
      setAssessment(getDefaultAssessment());
    } finally {
      setLoading(false);
    }
  };

  const runCheck = async () => {
    setRunning(true);
    await fetchAssessment();
    setRunning(false);
  };

  useEffect(() => { fetchAssessment(); }, []);

  const overall = assessment?.overall_score || 0;
  const level = getScoreLevel(overall);
  const config = SCORE_COLORS[level];

  return (
    <div className="space-y-6" data-testid="preflight-check-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-amber-400" />
            Pre-Flight Check
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Assess audit readiness across all active disclosure cycles
          </p>
        </div>
        <Button
          onClick={runCheck}
          disabled={running}
          className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
          data-testid="run-check-btn"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {running ? 'Running Check...' : 'Run Pre-Flight Check'}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        </div>
      ) : assessment && (
        <>
          {/* Overall Score */}
          <div className={`p-6 ${config.bg} border ${config.border} rounded-xl`} data-testid="overall-score">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-[#A0A0A0]">Overall Audit Readiness</p>
                <p className={`text-4xl font-bold ${config.color} mt-1`}>{overall}%</p>
              </div>
              <div className={`p-4 rounded-full ${config.bg}`}>
                {level === 'excellent' && <CheckCircle2 className={`w-10 h-10 ${config.color}`} />}
                {level === 'good' && <TrendingUp className={`w-10 h-10 ${config.color}`} />}
                {level === 'warning' && <AlertTriangle className={`w-10 h-10 ${config.color}`} />}
                {level === 'critical' && <XCircle className={`w-10 h-10 ${config.color}`} />}
              </div>
            </div>
            <Progress value={overall} className="h-2" />
            <p className="text-xs text-[#A0A0A0] mt-2">{assessment.summary || 'Assessment completed'}</p>
          </div>

          {/* Category Scores */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(assessment.categories || []).map((cat, i) => (
              <CategoryCard key={i} category={cat} />
            ))}
          </div>

          {/* Critical Issues */}
          {assessment.issues?.length > 0 && (
            <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#1F1F1F] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <h3 className="font-medium text-white">Issues Requiring Attention</h3>
                </div>
                <Badge className="bg-red-500/20 text-red-400 border-0">{assessment.issues.length}</Badge>
              </div>
              <div className="divide-y divide-[#1F1F1F]">
                {assessment.issues.map((issue, i) => (
                  <IssueRow key={i} issue={issue} />
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {assessment.recommendations?.length > 0 && (
            <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#1F1F1F] flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-400" />
                <h3 className="font-medium text-white">Recommendations</h3>
              </div>
              <div className="p-4 space-y-3">
                {assessment.recommendations.map((rec, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-[#0D0D0D] rounded-lg">
                    <ArrowRight className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-white">{rec.title}</p>
                      <p className="text-xs text-[#6B6B6B] mt-1">{rec.description}</p>
                    </div>
                    <Badge className="shrink-0 bg-[#1F1F1F] text-[#A0A0A0] border-0">{rec.priority}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CategoryCard({ category }) {
  const [expanded, setExpanded] = useState(false);
  const level = getScoreLevel(category.score);
  const config = SCORE_COLORS[level];
  const Icon = category.icon_name === 'evidence' ? Database
    : category.icon_name === 'ownership' ? Users
    : category.icon_name === 'completeness' ? FileText
    : category.icon_name === 'timeliness' ? Clock
    : category.icon_name === 'quality' ? BarChart3
    : CheckCircle2;

  return (
    <div className={`bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden`} data-testid={`category-${category.name?.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${config.bg}`}>
              <Icon className={`w-4 h-4 ${config.color}`} />
            </div>
            <span className="text-sm font-medium text-white">{category.name}</span>
          </div>
          <span className={`text-lg font-bold ${config.color}`}>{category.score}%</span>
        </div>
        <Progress value={category.score} className="h-1.5 mb-2" />
        <p className="text-xs text-[#6B6B6B]">{category.description}</p>
      </div>
      {category.checks?.length > 0 && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full px-4 py-2 border-t border-[#1F1F1F] flex items-center justify-between text-xs text-[#6B6B6B] hover:text-white transition-colors"
          >
            <span>{category.checks.length} checks</span>
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expanded && (
            <div className="px-4 pb-3 space-y-1.5">
              {category.checks.map((check, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {check.passed ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                  ) : (
                    <XCircle className="w-3 h-3 text-red-400" />
                  )}
                  <span className={check.passed ? 'text-[#A0A0A0]' : 'text-white'}>{check.name}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IssueRow({ issue }) {
  const severity = SCORE_COLORS[issue.severity === 'critical' ? 'critical' : issue.severity === 'high' ? 'warning' : 'good'];
  return (
    <div className="p-3 flex items-start gap-3">
      <AlertTriangle className={`w-4 h-4 ${severity.color} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white">{issue.title}</p>
        <p className="text-xs text-[#6B6B6B] mt-0.5">{issue.description}</p>
      </div>
      <Badge className={`${severity.bg} ${severity.color} border-0 shrink-0`}>{issue.severity}</Badge>
    </div>
  );
}

function getDefaultAssessment() {
  return {
    overall_score: 73,
    summary: 'Your platform is in good shape but has some gaps that should be addressed before the next audit cycle.',
    categories: [
      { name: 'Evidence Coverage', icon_name: 'evidence', score: 82, description: 'Evidence documents linked to disclosure requirements', checks: [
        { name: 'All ESRS requirements have linked evidence', passed: true },
        { name: 'Evidence freshness within 12 months', passed: true },
        { name: 'Cross-framework evidence reuse active', passed: false },
        { name: 'OCR extracted text validated', passed: true },
      ]},
      { name: 'Data Completeness', icon_name: 'completeness', score: 68, description: 'Required data points filled across frameworks', checks: [
        { name: 'CSRD mandatory fields complete', passed: true },
        { name: 'GHG Scope 1 & 2 data available', passed: true },
        { name: 'GHG Scope 3 categories filled', passed: false },
        { name: 'EU Taxonomy activity data entered', passed: false },
      ]},
      { name: 'Ownership & Approvals', icon_name: 'ownership', score: 85, description: 'Claims have designated owners and approval status', checks: [
        { name: 'All claims have assigned owners', passed: true },
        { name: 'Approval workflows configured', passed: true },
        { name: 'No orphaned claims detected', passed: true },
        { name: 'SLA for approvals met', passed: false },
      ]},
      { name: 'Timeliness', icon_name: 'timeliness', score: 60, description: 'Progress against disclosure deadlines', checks: [
        { name: 'CSRD cycle on track', passed: true },
        { name: 'CDP questionnaire deadline met', passed: false },
        { name: 'CSDDD obligations timeline', passed: false },
        { name: 'Internal review milestones', passed: true },
      ]},
      { name: 'Data Quality', icon_name: 'quality', score: 78, description: 'Anomaly detection and data validation scores', checks: [
        { name: 'No critical anomalies detected', passed: true },
        { name: 'Year-over-year variance within bounds', passed: true },
        { name: 'Unit consistency across metrics', passed: true },
        { name: 'Third-party data verified', passed: false },
      ]},
      { name: 'Audit Trail', icon_name: 'audit', score: 92, description: 'Immutable evidence chain from data to disclosure', checks: [
        { name: 'Hash chain integrity verified', passed: true },
        { name: 'All modifications logged', passed: true },
        { name: 'Evidence lineage complete', passed: true },
        { name: 'External auditor access configured', passed: true },
      ]},
    ],
    issues: [
      { title: 'Scope 3 Category 1 data missing for 3 suppliers', description: 'Purchased goods and services emissions not reported by key suppliers.', severity: 'critical' },
      { title: 'CDP Climate questionnaire 45% incomplete', description: 'Sections C4 (Targets) and C6 (Emissions data) require additional data.', severity: 'high' },
      { title: 'EU Taxonomy alignment assessment pending for 2 activities', description: 'Manufacturing and transport activities need DNSH assessment.', severity: 'high' },
      { title: '3 evidence documents older than 18 months', description: 'Energy consumption certificates need renewal.', severity: 'medium' },
    ],
    recommendations: [
      { title: 'Send supplier data collection surveys', description: 'Use the Supplier Portal to collect Scope 3 emissions data from your top 10 suppliers.', priority: 'P0' },
      { title: 'Complete EU Taxonomy DNSH assessments', description: 'Review and complete Do No Significant Harm assessments for manufacturing activities.', priority: 'P1' },
      { title: 'Refresh expired evidence documents', description: 'Upload updated energy consumption certificates to maintain evidence freshness.', priority: 'P2' },
    ],
  };
}
